// توکن‌های طراحی، با دو پالتِ قابل تعویض در زمان اجرا.
//
// قبلاً یه شیء ثابت بود. حالا کاربر خودش تم رو انتخاب می‌کنه، پس رنگ‌ها باید
// وسطِ کار عوض بشن. الگو عیناً همون i18n است: یه شیءِ زنده که **در جا** پر
// می‌شه (نه جایگزین)، به‌علاوه‌ی شنونده‌هایی که ریشه رو دوباره رندر می‌کنن.
//
// چرا در جا و نه جایگزین: همه‌ی فایل‌ها `const C = theme.color` رو بالای ماژول
// می‌گیرن. اگه شیء رو عوض می‌کردیم، اون‌ها به نسخه‌ی قدیمی چسبیده می‌موندن.
//
// یه دام: StyleSheet.create مقدارها رو موقع ساخت کپی می‌کنه. هر فایلی که
// استایلِ ماژول‌سطحی داره باید با `themeVersion()` بازسازیش کنه — نگاه کن به kit.tsx.
//
// از فاز ۳: خودِ مقادیرِ رنگ و اندازه دیگه اینجا نیستن — رفتن به
// `@palsandplans/tokens` تا سایت هم از همون‌ها بخونه. این فایل فقط منطقِ «کدوم پالت
// الان فعاله و چطور عوض می‌شه» رو نگه داشته.
import { storage } from '../platform.ts';
import { recordPref } from './prefSink.ts';
import { PALETTES, radius, space } from '@palsandplans/tokens';
import type { ThemeName } from '@palsandplans/tokens';

export type { ThemeName, Palette } from '@palsandplans/tokens';
export { PALETTES } from '@palsandplans/tokens';

let current: ThemeName = 'night';
let version = 0;
const listeners = new Set<() => void>();

/**
 * «کاربر خودش انتخاب کرده» — جدا از `current`، چون `current` حتی وقتی هیچ
 * انتخابی نشده هم یه مقدار داره (حدسِ initTheme از روی تنظیمِ گوشی).
 *
 * چرا در حافظه و نه فقط در storage: نوشتنِ storage async است و منتظرش
 * نمی‌مانیم. کاربر تم را می‌زند و بلافاصله «ادامه» را می‌زند؛ صفحه‌ی `/`
 * همان لحظه `hasPickedTheme()` را می‌پرسد و روی گوشیِ کند هنوز چیزی ننشسته
 * — جوابِ `false` او را به همان صفحه‌ی انتخاب برمی‌گرداند. نگاه کن به
 * WHOSUP-206.
 */
let userPicked = false;

/** شیءِ زنده. هیچ‌وقت جایگزین نمیشه، فقط پر میشه. */
export const theme = {
  color: { ...PALETTES.night },
  radius,
  space,
};
export type Theme = typeof theme;

/** هر بار که تم عوض شه یکی بالا می‌ره؛ کلیدِ بازسازیِ StyleSheetهاست. */
export const themeVersion = (): number => version;
export const getTheme = (): ThemeName => current;
export const isDarkTheme = (): boolean => theme.color.isDark;

export const onThemeChange = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

const apply = (name: ThemeName) => {
  current = name;
  Object.assign(theme.color, PALETTES[name]);
  version += 1;
  listeners.forEach((fn) => fn());
};

const STORE_KEY = 'goout.theme.v1';

/**
 * «این تم از حالا تنظیمِ این دستگاه است» — روی حافظه می‌نشیند و همان لحظه
 * در `userPicked` هم ثبت می‌شود، بی‌آنکه منتظرِ نوشتن بمانیم (WHOSUP-206).
 */
const remember = (name: ThemeName): void => {
  userPicked = true;
  storage().setItem(STORE_KEY, name).catch(() => undefined);
};

export const setTheme = (name: ThemeName): void => {
  apply(name);
  remember(name);
  // فقط ثبت — تم همچنان از همین حافظه‌ی دستگاه خوانده می‌شود و سرور
  // هیچ‌وقت رویش حرف نمی‌زند. نگاه کن به lib/prefSink.
  recordPref('theme', name);
};

/**
 * آیا کاربر تا حالا تم انتخاب کرده؟ صفحه‌ی خوش‌آمد فقط یک‌بار باید بپرسه.
 * جدا از initTheme است، چون initTheme حتی وقتی چیزی ذخیره نشده هم باید یه پالت
 * بذاره (حدس از روی تنظیمِ گوشی) تا اپ بی‌رنگ بالا نیاد.
 */
export const hasPickedTheme = async (): Promise<boolean> => {
  if (userPicked) return true;
  try {
    return (await storage().getItem(STORE_KEY)) !== null;
  } catch {
    return false;
  }
};

export interface InitThemeOptions {
  /**
   * وقتی دستگاه خودش گفته روشن است یا تیره، همان را **تنظیمِ کاربر** حساب کن
   * و دیگر نپرس (WHOSUP-214).
   *
   * چرا یک گزینه و نه رفتارِ همیشگی: فقط موبایل سرِ نصب سوال می‌پرسد، و آن
   * سوال وقتی جواب از خودِ گوشی درمی‌آید بی‌معنی است. سایت اصلاً نمی‌پرسد و
   * در عوض هر بار همراهِ تنظیمِ سیستم عوض می‌شود؛ نشاندنِ یک مقدارِ ماندگار
   * آن‌جا دنبال‌کردنِ سیستم را برای همیشه خاموش می‌کرد.
   *
   * اگر دستگاه جوابی نداشته باشد (`prefersDark === null`) این گزینه کاری
   * نمی‌کند و صفحه‌ی انتخاب سرِ جایش می‌ماند.
   */
  adoptDeviceTheme?: boolean;
}

/**
 * قبل از اولین رندر صدا زده میشه. اگه کاربر انتخابی نکرده، از حالتِ روشن/تیره‌ی
 * خودِ گوشی حدس می‌زنیم — حدسی که بعداً توی صفحه‌ی خوش‌آمد تایید یا عوض میشه،
 * یا با `adoptDeviceTheme` همان‌جا قطعی می‌شود.
 *
 * `prefersDark`: اپ خودش تشخیص میده (موبایل با Appearance، وب با
 * matchMedia) و اینجا فقط جواب رو می‌گیریم — core نباید بدونه این تشخیص با
 * کدوم API انجام شده. `null` یعنی دستگاه جواب نداد.
 */
export const initTheme = async (
  prefersDark: boolean | null,
  opts: InitThemeOptions = {},
): Promise<void> => {
  let saved: string | null = null;
  try {
    saved = await storage().getItem(STORE_KEY);
  } catch { /* حافظه در دسترس نیست */ }

  if (saved === 'night' || saved === 'coral') { userPicked = true; apply(saved); return; }
  // دستگاه جواب نداده: تیره را می‌گذاریم تا اپ بی‌رنگ بالا نیاد، ولی این
  // همچنان یک حدس است و صفحه‌ی خوش‌آمد باید بپرسد.
  if (prefersDark === null) { apply('night'); return; }

  const fromDevice: ThemeName = prefersDark ? 'night' : 'coral';
  apply(fromDevice);
  // ⚠ `remember` و نه `setTheme`: این تصمیمِ کاربر نیست، پس در
  // `user_pref_events` هم نباید به‌عنوانِ تصمیم بنشیند — همان تفکیکی که
  // `pickedLocale()` برای زبان قائل می‌شود.
  if (opts.adoptDeviceTheme) remember(fromDevice);
};
